/**
 * The single source of verdict rendering. Every pass/warn/fail/na in the
 * product renders through this component.
 *
 * That is a hard constraint, not a preference: forty of these share a screen in
 * the compliance matrix, so one inconsistency is immediately visible, and
 * verdict rendering is the product's core visual vocabulary.
 *
 * ACCESSIBILITY RULE (DESIGN-SYSTEM §1): status is never conveyed by colour
 * alone. Label first, glyph second, colour third. The tinted fill is decorative
 * — do not remove the text label to save space.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type Verdict = 'pass' | 'warn' | 'fail' | 'na' | 'info';

const LABELS: Record<Verdict, string> = {
  pass: 'Compliant',
  warn: 'Attention',
  fail: 'Gap',
  na: 'N/A',
  info: 'Info',
};

/* Fixed vocabulary — see BRAND.md §4. Do not paraphrase per screen. */
const RGB_VAR: Record<Verdict, string> = {
  pass: '--success-rgb',
  warn: '--warn-rgb',
  fail: '--danger-rgb',
  na: '--na-rgb',
  info: '--info-rgb',
};

const FG_VAR: Record<Verdict, string> = {
  pass: '--success',
  warn: '--warn',
  fail: '--danger',
  na: '--muted-fg',
  info: '--info',
};

/**
 * TEXT colour, which is not the same thing as the verdict colour.
 *
 * The badge painted its label in `FG_VAR` over a 12% tint of the same token. Measured on the
 * composite in light theme that is 4.25:1 for `warn` — under the 4.5:1 floor — and green is
 * worse: `--success` #16a34a is only 3.36:1 on plain white before any tint. The label is the
 * word "Warn"/"Pass"/"Fail" itself, so it is the part that must be readable; the tint, the
 * border and the glyph carry the colour and are graphics, held to 3:1 by WCAG 1.4.11.
 *
 * In dark theme the ink tokens resolve to the same bright hues, because on a dark tint they
 * already clear 4.5:1 — see tokens.css.
 */
const INK_VAR: Record<Verdict, string> = {
  pass: '--success-ink',
  warn: '--warn-ink',
  fail: '--danger-ink',
  na: '--muted-fg',
  info: '--info-ink',
};

function Glyph({ verdict }: { verdict: Verdict }): ReactNode {
  const common = {
    width: 12,
    height: 12,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    style: { flex: 'none' },
  };

  if (verdict === 'pass') {
    return (
      <svg {...common}>
        <path d="M20 6 9 17l-5-5" />
      </svg>
    );
  }
  if (verdict === 'fail') {
    return (
      <svg {...common}>
        <path d="M12 9v4M12 17h.01" />
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      </svg>
    );
  }
  if (verdict === 'warn') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v4M12 16h.01" />
      </svg>
    );
  }
  return null;
}

export interface StatusBadgeProps {
  verdict: Verdict;
  /** Overrides the fixed label. Use sparingly — the vocabulary is deliberate. */
  label?: string;
  className?: string;
}

export function StatusBadge({
  verdict,
  label,
  className,
}: StatusBadgeProps): ReactNode {
  const text = label ?? LABELS[verdict];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-[5px] rounded-[var(--radius-pill)]',
        'px-[9px] py-[2px] text-[11.5px] font-semibold whitespace-nowrap',
        className,
      )}
      style={{
        background: `rgb(var(${RGB_VAR[verdict]}) / 12%)`,
        border: `1px solid rgb(var(${RGB_VAR[verdict]}) / 25%)`,
        color: `var(${INK_VAR[verdict]})`,
      }}
    >
      <Glyph verdict={verdict} />
      {text}
    </span>
  );
}

/**
 * The dot, for dense lists where a badge will not fit — AND IT IS NOT JUST A DOT ANY MORE.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY EACH VERDICT GETS ITS OWN SILHOUETTE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `StatusBadge` above has always satisfied DESIGN-SYSTEM §1 — status is never conveyed by
 * colour alone — because it carries a word and a glyph. This did not: it was one round dot
 * in five colours, so every reader who cannot separate those hues got no information from it
 * at all. That was already true for the 8% of men with a red-green deficiency looking at
 * `pass` beside `fail`.
 *
 * The Cratus palette made it sharper. Its brand is an amber at hue 37°, and `--warn` sits at
 * 43° — six degrees apart, which is not a difference anybody resolves at 8 pixels. A warn dot
 * and any brand-coloured dot in the same header are now the same mark unless the SHAPE says
 * otherwise.
 *
 * The three shapes that matter are the design language's own, which separates its severities
 * exactly this way: a rotated square for critical, a filled circle for major, a hollow ring
 * for the lowest band. `info` and `na` take the two remaining silhouettes that read at this
 * size — a filled square and a bar. Five marks, distinguishable with the colour removed.
 *
 * The bar for `na` is the one worth defending: "not applicable" is not a quiet version of a
 * verdict, it is the absence of one, and a dash is what that looks like. It also cannot be
 * mistaken for a small dot, which is how "not measured" starts looking like "fine" — the
 * failure the guidelines name directly.
 */
type DotShape = 'diamond' | 'circle' | 'ring' | 'square' | 'bar';

const SHAPE: Record<Verdict, DotShape> = {
  fail: 'diamond',
  warn: 'circle',
  pass: 'ring',
  info: 'square',
  na: 'bar',
};

function shapeStyle(shape: DotShape, colour: string): Record<string, string | number> {
  if (shape === 'ring') {
    return { width: 8, height: 8, borderRadius: '50%', border: `2px solid ${colour}` };
  }
  if (shape === 'bar') {
    return { width: 8, height: 2.5, borderRadius: 2, background: colour };
  }
  if (shape === 'square') {
    return { width: 7, height: 7, borderRadius: 1.5, background: colour };
  }
  if (shape === 'diamond') {
    /* 6px so the 8.5px diagonal still sits inside the 8px box every caller lays out around. */
    return { width: 6, height: 6, borderRadius: 1, background: colour, transform: 'rotate(45deg)' };
  }
  return { width: 8, height: 8, borderRadius: '50%', background: colour };
}

export function StatusDot({
  verdict,
  className,
}: {
  verdict: Verdict;
  className?: string;
}): ReactNode {
  const shape = SHAPE[verdict];
  return (
    <span
      role="img"
      aria-label={LABELS[verdict]}
      /* The 8px box is fixed whatever the silhouette inside it, so a column of these stays a
         column — a rotated square that changed the line box would nudge every row it sat in. */
      className={cn('inline-grid size-2 shrink-0 place-items-center', className)}
    >
      <span
        aria-hidden
        style={{
          ...shapeStyle(shape, `var(${FG_VAR[verdict]})`),
          boxShadow: `0 0 0 3px rgb(var(${RGB_VAR[verdict]}) / 15%)`,
        }}
      />
    </span>
  );
}
